import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaDate,
  type CitationParts,
  mlaDate,
  type PartialDate,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  fullDate,
  get,
  italic,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import type { SpecialValues } from "@/lib/tool-kit/special-sources/common";

/*
 * Traditional Knowledge and Oral Traditions shared by Indigenous Elders and Knowledge
 * Keepers: APA 7's personal-communication variation, the Elder and Knowledge Keeper
 * templates by Lorisia MacLeod (James Smith Cree Nation) and the NorQuest College
 * Indigenous Student Centre, and APA's rule for teachings that were recorded.
 *
 * Sources checked (October 2026):
 * - APA Style, "Personal communications" (Publication Manual §8.9; page last updated
 *   July 2022): recorded and recoverable knowledge is cited in the format of that
 *   source (a video, a book…) with a reference list entry; unrecorded knowledge from a
 *   person you spoke with is a variation of the personal communication citation —
 *   "Provide the person's full name and the nation or specific Indigenous group to
 *   which they belong, as well as their location or other details about them as
 *   relevant, followed by the words 'personal communication,' and the date of the
 *   communication"; an exact date if available, otherwise a general date or a range;
 *   the person agrees to be named and confirms accuracy; no reference list entry.
 *   APA's example: "… (Haida Nation, lives in Vancouver, British Columbia, Canada,
 *   personal communication, April 2019) …". Elder, Knowledge Keeper, Oral Tradition
 *   and Traditional Knowledge are capitalized.
 * - MacLeod, L. (2021). More than personal communication: Templates for citing
 *   Indigenous Elders and Knowledge Keepers. KULA, 5(1).
 *   https://doi.org/10.18357/kula.135 — the templates' elements and reasons.
 * - Their current published form, NorQuest College Library (fetched October 5, 2026):
 *   APA — "Last name, First initial., Nation/Community. Treaty Territory if
 *   applicable. Where they live if applicable. Topic/subject of communication if
 *   applicable. personal communication. Month Date, Year." (in text: "(Cardinal,
 *   2018)" or the full name in the sentence); MLA — "Last name, First name.,
 *   Nation/Community. … Date Month Year." with "4 Apr. 2004" (in text: "(Cardinal)").
 *   This generator writes the MLA name as "Last name, First name," without the period
 *   that NorQuest's example prints after the first name.
 * - A recorded teaching online follows APA's video and audio formats (§10.12–10.14;
 *   the uploader is the author, with a bracketed "[Video]" or "[Audio recording]")
 *   and MLA's core elements (the speaker as author, "uploaded by" the account).
 */

export const INDIGENOUS_KNOWLEDGE_FIELDS = {
  firstName: "firstName",
  lastName: "lastName",
  nation: "nation",
  treaty: "treaty",
  residence: "residence",
  topic: "topic",
  date: "date",
  uploader: "uploader",
  title: "title",
  medium: "medium",
  site: "site",
  url: "url",
} as const;

const F = INDIGENOUS_KNOWLEDGE_FIELDS;

export type KnowledgeSource = "unrecorded" | "recorded";

export interface IndigenousKnowledgeLabels {
  apa: string;
  templateApa: string;
  templateMla: string;
  mla: string;
  referenceList: string;
  worksCited: string;
  parenthetical: string;
  narrative: string;
  inText: string;
  namingSpeaker: string;
  apaNote: string;
  templateApaNote: string;
  templateMlaNote: string;
  recordedApaNote: string;
  recordedMlaNote: string;
}

const RE_YEAR = /\d{4}/;

interface SharedDate {
  /** "April 4, 2024", or the range as typed. */
  full: string;
  /** "4 Apr. 2024", or the range as typed. */
  mla: string;
  year: string;
}

/** An exact or partial date, or a range or season typed as words ("spring 2019"). */
function sharedDate(raw: string): SharedDate {
  const date: PartialDate | null = parsePartialDate(raw);
  if (date) {
    return {
      full: fullDate(date),
      mla: mlaDate(date),
      year: String(date.year),
    };
  }
  return { full: raw, mla: raw, year: RE_YEAR.exec(raw)?.[0] ?? "n.d." };
}

interface Speaker {
  first: string;
  last: string;
}

function speakerOf(values: SpecialValues): Speaker {
  return { first: get(values, F.firstName), last: get(values, F.lastName) };
}

/** "Firstname Lastname" — APA asks for the full name, in the person's own order. */
function fullName({ first, last }: Speaker): string {
  return [first, last].filter(Boolean).join(" ");
}

/** "Lastname, F." — or a single name as written when there is no last name. */
function templateApaName({ first, last }: Speaker): string {
  if (last === "" || first === "") {
    return last || first;
  }
  const initials = first
    .split(" ")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}.`)
    .join(" ");
  return `${last}, ${initials}`;
}

/** "Lastname, Firstname" — or a single name as written. */
function templateMlaName({ first, last }: Speaker): string {
  if (last === "" || first === "") {
    return last || first;
  }
  return `${last}, ${first}`;
}

/** The family name for an in-text citation, or the whole name if there is only one. */
function citedName({ first, last }: Speaker): string {
  return last || first;
}

export function indigenousKnowledgeIsReady(
  values: SpecialValues,
  source: KnowledgeSource
): boolean {
  if (source === "recorded") {
    return get(values, F.title) !== "";
  }
  return fullName(speakerOf(values)) !== "" && get(values, F.nation) !== "";
}

/* ----------------------------------------------------------- unrecorded */

/** "Nation, Treaty 6, lives in Edmonton" — APA's details after the name. */
function apaDetails(values: SpecialValues): string {
  const residence = get(values, F.residence);
  return [
    get(values, F.nation),
    get(values, F.treaty),
    residence ? `lives in ${residence}` : "",
  ]
    .filter(Boolean)
    .join(", ");
}

/** The template's elements after the name, each closed with a period. */
function templateElements(values: SpecialValues): string[] {
  const residence = get(values, F.residence);
  return [
    get(values, F.nation),
    get(values, F.treaty),
    residence ? `Lives in ${residence}` : "",
    get(values, F.topic),
  ]
    .filter(Boolean)
    .map((element) => `${element}${closer(element)}`);
}

function formatUnrecorded(
  values: SpecialValues,
  labels: IndigenousKnowledgeLabels
): CitationOutput[] {
  const speaker = speakerOf(values);
  const name = fullName(speaker);
  const date = sharedDate(get(values, F.date));
  const when = date.full ? `, ${date.full}` : "";
  const details = apaDetails(values);
  const apaName = templateApaName(speaker);
  const apaTemplate = [
    `${apaName},`,
    ...templateElements(values),
    "personal communication.",
    date.full ? `${date.full}${closer(date.full)}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const mlaName = templateMlaName(speaker);
  const mlaTemplate = [
    `${mlaName},`,
    ...templateElements(values),
    date.mla ? `${date.mla}${closer(date.mla)}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  return [
    {
      key: "apa",
      label: labels.apa,
      entries: [
        {
          label: labels.parenthetical,
          parts: [
            plain(`(${name}, ${details}, personal communication${when})`),
          ],
        },
        {
          label: labels.narrative,
          parts: [plain(`${name} (${details}, personal communication${when})`)],
        },
      ],
      note: labels.apaNote,
    },
    {
      key: "template-apa",
      label: labels.templateApa,
      entries: [
        { label: labels.referenceList, parts: [plain(apaTemplate)] },
        {
          label: labels.parenthetical,
          parts: [plain(`(${citedName(speaker)}, ${date.year})`)],
        },
        {
          label: labels.narrative,
          parts: [plain(`${name} (${date.year})`)],
        },
      ],
      note: labels.templateApaNote,
    },
    {
      key: "template-mla",
      label: labels.templateMla,
      entries: [
        { label: labels.worksCited, parts: [plain(mlaTemplate)] },
        { label: labels.inText, parts: [plain(`(${citedName(speaker)})`)] },
      ],
      note: labels.templateMlaNote,
    },
  ];
}

/* ------------------------------------------------------------- recorded */

function formatRecorded(
  values: SpecialValues,
  labels: IndigenousKnowledgeLabels
): CitationOutput[] {
  const speaker = speakerOf(values);
  const name = fullName(speaker);
  const nation = get(values, F.nation);
  const uploader = get(values, F.uploader);
  const title = get(values, F.title);
  const site = get(values, F.site);
  const url = get(values, F.url);
  const date = parsePartialDate(get(values, F.date));
  const year = date ? String(date.year) : "n.d.";
  const medium =
    get(values, F.medium) === "audio" ? "Audio recording" : "Video";

  // APA credits the account that published the recording; with none, the speaker.
  const apaAuthor = uploader || name;
  const source = [site && site !== apaAuthor ? `${site}.` : "", url]
    .filter(Boolean)
    .join(" ");
  const apaReference: CitationParts = apaAuthor
    ? [
        plain(`${apaAuthor}${closer(apaAuthor)} (${apaDate(date)}). `),
        italic(title),
        plain(` [${medium}]. ${source}`),
      ]
    : [italic(title), plain(` [${medium}]. (${apaDate(date)}). ${source}`)];

  const mla: CitationParts = [];
  if (name) {
    const mlaName = templateMlaName(speaker);
    mla.push(plain(`${mlaName}${closer(mlaName)} `));
  }
  mla.push(plain(`“${title}${closer(title)}” `));
  const container = [
    uploader && uploader !== name ? `uploaded by ${uploader}` : "",
    mlaDate(date),
    url ? mlaUrl(url) : "",
  ].filter(Boolean);
  if (site) {
    mla.push(italic(site));
    mla.push(plain(container.length > 0 ? `, ${container.join(", ")}.` : "."));
  } else if (container.length > 0) {
    mla.push(plain(`${container.join(", ")}.`));
  }

  const apaEntries = [
    { label: labels.referenceList, parts: tidyParts(apaReference) },
    {
      label: labels.parenthetical,
      parts: [plain(`(${apaAuthor || title}, ${year})`)],
    },
  ];
  if (name && nation) {
    apaEntries.push({
      label: labels.namingSpeaker,
      parts: [plain(`${name} (${nation})`)],
    });
  }
  return [
    {
      key: "apa",
      label: labels.apa,
      entries: apaEntries,
      note: labels.recordedApaNote,
    },
    {
      key: "mla",
      label: labels.mla,
      entries: [
        { label: labels.worksCited, parts: tidyParts(mla) },
        {
          label: labels.inText,
          parts: [plain(name ? `(${citedName(speaker)})` : `(“${title}”)`)],
        },
      ],
      note: labels.recordedMlaNote,
    },
  ];
}

export function formatIndigenousKnowledge(
  values: SpecialValues,
  source: KnowledgeSource,
  labels: IndigenousKnowledgeLabels
): CitationOutput[] {
  return source === "recorded"
    ? formatRecorded(values, labels)
    : formatUnrecorded(values, labels);
}
